import { Badge } from '@ipfabric/ui-components/Badge/Badge';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { Select } from '@ipfabric/ui-components/Select/Select';
import { Switch } from '@ipfabric/ui-components/Switch/Switch';
import { TextInput } from '@ipfabric/ui-components/TextInput/TextInput';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { useMemo, useState } from 'react';
import type { LabelEnrolment } from '../dataPoliciesApi';
import { isLabelInUse } from './useEnrolmentDraft';

type SortKey = 'name' | 'entities';

const SORT_OPTIONS = [
  { label: 'Sort: name', value: 'name' },
  { label: 'Sort: entities', value: 'entities' },
] as const satisfies { label: string; value: SortKey }[];

const policyNames = (entry: LabelEnrolment) =>
  entry.policies.map((policy) => policy.name).join(', ');

type EnrolledLabelsListProps = {
  entries: readonly LabelEnrolment[];
  draft: ReadonlySet<string>;
  onToggle: (entry: LabelEnrolment, enrolled: boolean) => void;
  /** Without the enrolment write scope the switches only show the state. */
  readOnly: boolean;
};

export const EnrolledLabelsList = ({
  entries,
  draft,
  onToggle,
  readOnly,
}: EnrolledLabelsListProps) => {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('name');

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return entries
      .filter((entry) => entry.name.toLowerCase().includes(needle))
      .sort((a, b) =>
        sort === 'entities'
          ? b.entityCount - a.entityCount || a.name.localeCompare(b.name)
          : a.name.localeCompare(b.name),
      );
  }, [entries, search, sort]);

  return (
    <Card className='flex flex-col overflow-hidden p-0'>
      <div className='flex gap-xs border-b border-default px-sm pb-xs pt-sm shadow-sm'>
        <div className='flex-1'>
          <TextInput
            onChange={(event) => setSearch(event.target.value)}
            placeholder='Search labels'
            startAdornment={<Icon glyph='fa-magnifying-glass' size='sm' />}
            type='text'
            value={search}
          />
        </div>
        <Select.Mandatory<SortKey>
          options={SORT_OPTIONS}
          value={sort}
          onChange={setSort}
          size='default'
        />
      </div>
      <div className='flex flex-col'>
        {rows.map((entry) => (
          <EnrolmentRow
            key={entry.id}
            entry={entry}
            enrolled={draft.has(entry.id)}
            readOnly={readOnly}
            onToggle={(enrolled) => onToggle(entry, enrolled)}
          />
        ))}
        {rows.length === 0 && (
          <span className='px-sm py-sm text-element-secondary'>
            No labels found.
          </span>
        )}
      </div>
    </Card>
  );
};

type EnrolmentRowProps = {
  entry: LabelEnrolment;
  enrolled: boolean;
  readOnly: boolean;
  onToggle: (enrolled: boolean) => void;
};

const EnrolmentRow = ({
  entry,
  enrolled,
  readOnly,
  onToggle,
}: EnrolmentRowProps) => {
  const locked = isLabelInUse(entry);
  const usage =
    entry.policies.length > 0
      ? `Used in: ${policyNames(entry)}`
      : enrolled
        ? 'not used yet'
        : null;
  const toggle = (
    <Switch
      dataTestId={`enrolled-label-switch-${entry.name}`}
      disabled={locked || readOnly}
      value={enrolled}
      onChange={onToggle}
    />
  );

  return (
    <div className='flex items-center gap-xs border-b border-default px-sm py-xs'>
      <div className='flex flex-1 flex-col'>
        <span>{entry.name}</span>
        {usage && (
          <span className='text-sm text-element-secondary'>{usage}</span>
        )}
      </div>
      <Badge color='neutral' variant='plain'>
        {entry.entityCount}
      </Badge>
      {locked ? (
        // A label used by data policies cannot be un-enrolled.
        <Tooltip content='Used by data policies. Remove it from those policies before un-enrolling.'>
          <span>{toggle}</span>
        </Tooltip>
      ) : (
        toggle
      )}
    </div>
  );
};
